import React from 'react';
import { Box, CircularProgress, Typography, Backdrop } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * A full-page loading screen with a centered spinner and optional message.
 * Use this for initial app loading or major route transitions.
 */
export const LoadingScreen: React.FC<{ message?: string }> = ({ message }) => {
    const { t } = useTranslation();
    return (
        <Backdrop
            open={true}
            sx={{
                zIndex: (theme) => theme.zIndex.appBar - 1,
                color: '#fff',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            <CircularProgress color="primary" size={48} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                {message || t('common.loading', 'Loading...')}
            </Typography>
        </Backdrop>
    );
};

/**
 * A centered loading spinner for use within a container or section.
 */
export const LoadingBox: React.FC<{ height?: string | number; message?: string }> = ({ height = '50vh', message }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: height,
                gap: 2
            }}
        >
            <CircularProgress size={40} thickness={4} />
            {message && (
                <Typography variant="body2" color="text.secondary">
                    {message}
                </Typography>
            )}
        </Box>
    );
};
